import React, { useState, useEffect, useMemo } from 'react'
import PropTypes from 'prop-types'
import { makeStyles } from '@mui/styles'
import lang from '@/lang'
import moment from 'moment'
import { components } from 'react-select'
import { times } from 'lodash'
import clsx from 'clsx'
import config from 'config'
import DatevService from '../datevService'
import datevConfig from '../config'
import { Checkbox, SelectInput } from '@/modules/base/components/inputs/index'
import { ButtonIconSquare } from '@/modules/base/components/buttons/index'
import IconCheck from '@assets/images/icons/check.svg'
import { Label } from '@/modules/base/components/texts/index'
import useCheckModuleAccess from '@/modules/_helpers/accessCheck/useCheckModuleAccess'

const useStyles = makeStyles(theme => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    '& > *': {
      marginTop: 10,
    },
    '& :first-child': {
      marginTop: 0,
    },
  },
  checkbox: {
    marginTop: 20,
  },
  marginLeft: {
    marginLeft: 20,
  },
  checkboxLabel: {
    paddingLeft: 10,
    fontSize: theme.fontSizes.default,
  },
  action: {
    display: 'flex',
    justifyContent: 'center',
    paddingTop: 10,
    paddingBottom: 10,
    marginBottom: 10,
  },
  transferLabel: {
    marginTop: 20,
  },
  contentLabel: {
    marginTop: 20,
  },
  contentCheckboxesContainer: {
    display: 'flex',
  },
  automateContainer: {
    display: 'flex',
    '& label': {
      paddingRight: 10,
    },
  },
  rowInput: {
    display: 'flex',
    width: '100%',
  },
  yearInput: {
    width: 150,
    paddingRight: 30,
  },
  periodInput: {
    width: '100%',
  },
}))

const { dateFormat } = config
const { documentTypes, content, requiredScopesForContent } = datevConfig

const documentTypesNeedBookingEntries = [
  documentTypes.bankBookings,
  documentTypes.customersVendors,
]

const noUpdateInfoForDocumentTypes = [
  documentTypes.customersVendors,
]

const createOption = (label, fromDate, toDate) => {
  return { value: `${fromDate.unix()}-${toDate.unix()}`, label, fromDate: fromDate.format(dateFormat.api), toDate: toDate.format(dateFormat.api) }
}

const generatePeriodOptions = (year) => {
  const result = []
  times(12, (count) => {
    const date = moment(`${year}-${count + 1}-01`, 'YYYY-MM-DD')
    result.push(createOption(date.format('MMMM YYYY'), date.clone().startOf('month'), date.clone().endOf('month')))
  })
  return result
}

const DatevTransferView = props => {
  const { onSubmit, className, readOnly, availableServiceScopes, autoSyncActive, updateAutoSyncActive, useDocumentType, updateUseDocumentType } = props
  const initYear = moment().subtract('year').year()
  const classes = useStyles()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [resend, setResend] = useState(false)
  const [selectedContent, setSelectedContent] = useState([])
  const [selectedDocumentTypes, setSelectedDocumentTypes] = useState(Object.values(documentTypes))
  const [periodOptions, setPeriodOptions] = useState(generatePeriodOptions(initYear))
  const [period, setPeriod] = useState(periodOptions?.[0])
  const [newDocumentsCount, setNewDocumentsCount] = useState(0)
  const [newBookingEntriesCount, setNewBookingEntriesCount] = useState(0)
  const [isLoading, setIsLoading] = useState(false)
  const [shouldUpdateTransferInfo, setShouldUpdateTransferInfo] = useState()
  const [year, setYear] = useState(initYear)

  const hasModuleAccessForBookingEntries = useCheckModuleAccess(content.bookingEntries.subscriptionModules)
  const hasModuleAccessForDocuments = useCheckModuleAccess(content.documents.subscriptionModules)

  const handleSendClick = () => {
    setIsSubmitting(true)
    const result = onSubmit && onSubmit({
      date: period.fromDate,
      resendExisting: resend,
      useDocumentType,
      documentTypes: selectedDocumentTypes,
      content: selectedContent,
    })
    Promise.resolve(result).finally(() => {
      setNewDocumentsCount(0)
      setNewBookingEntriesCount(0)
      setIsSubmitting(false)
    })
  }

  const handleYearChange = (event) => {
    setYear(event.value)
  }

  const currentYear = new Date().getFullYear()
  const yearInputOptions = Array.from({ length: 4 }, (v, i) => currentYear - i).map(year => ({
    value: year,
    label: year.toString(),
  }))

  const updateTransferInfo = () => {
    setIsLoading(true)
    DatevService.getTransferInfo({
      date: period.fromDate,
      resendExisting: resend,
      useDocumentType,
      documentTypes: selectedDocumentTypes,
      content: selectedContent,
    }).then(res => {
      setNewDocumentsCount(res.meta.documents.count || 0)
      setNewBookingEntriesCount(res.meta.bookingEntires.count || 0)
    }).finally(() => setIsLoading(false))
  }

  useEffect(() => {
    updateTransferInfo()
  }, [period, resend, shouldUpdateTransferInfo, selectedContent])

  useEffect(() => {
    updateTransferInfo()
  }, [])

  useEffect(() => {
    setPeriodOptions(generatePeriodOptions(year))
  }, [year])

  useEffect(() => {
    setPeriod(periodOptions[0])
  }, [periodOptions])

  const updateSelectedDocumentTypes = (type) => {
    if (selectedDocumentTypes.includes(type)) {
      setSelectedDocumentTypes([...selectedDocumentTypes.filter(filter => filter !== type)])
    } else {
      setSelectedDocumentTypes([...selectedDocumentTypes, type])
    }

    if (!noUpdateInfoForDocumentTypes.includes(type)) {
      setShouldUpdateTransferInfo(new Date().getTime())
    }
  }

  const availableContent = useMemo(() => {
    const isContentAvailable = (content) => {
      const scopesForContent = requiredScopesForContent[content.value]
      if (!scopesForContent || !(hasModuleAccessForBookingEntries || hasModuleAccessForDocuments)) return false

      const hasAllRequiredScopes = scopesForContent.every(requiredScope => availableServiceScopes.includes(requiredScope))
      return hasAllRequiredScopes
    }

    return Object.values(content).filter(isContentAvailable).map(c => c.value)
  }, [availableServiceScopes, hasModuleAccessForBookingEntries, hasModuleAccessForDocuments])

  const updateSelectedContent = (content) => {
    if (!availableContent.includes(content)) return

    if (selectedContent.includes(content)) {
      setSelectedContent([...selectedContent.filter(filter => filter !== content)])
    } else {
      setSelectedContent([...selectedContent, content])
    }
  }

  useEffect(() => {
    setSelectedContent(availableContent)
  }, [availableContent])

  const documentContentCheckboxes = Object.values(content).map(({ value: item }) => (
    <Checkbox key={item}
      name={item}
      customClass={clsx(classes.marginLeft)}
      checked={selectedContent.includes(item)}
      onChange={() => updateSelectedContent(item)}
      disabled={!availableContent.includes(item)}>
      <span className={classes.checkboxLabel}>{lang.accounting.content[item]}</span>
    </Checkbox>
  ))

  const documentTypeCheckboxesDisabled = (documentTypes) => {
    if (selectedContent.length === 0) {
      return true
    }

    if (documentTypesNeedBookingEntries.includes(documentTypes)) {
      return !selectedContent.includes(content.bookingEntries.value)
    }
  }

  const documentTypeCheckboxes = Object.values(documentTypes).map(documentType => {
    const isDisabled = documentTypeCheckboxesDisabled(documentType)

    return (
      <Checkbox key={documentType}
        name={documentType}
        customClass={clsx(classes.checkbox, classes.marginLeft)}
        checked={!isDisabled && selectedDocumentTypes.includes(documentType)}
        onChange={() => updateSelectedDocumentTypes(documentType)}
        disabled={isDisabled}
      >
        <span className={classes.checkboxLabel}>{lang.accounting.documentTypes[documentType]}</span>
      </Checkbox>
    )
  })

  return (
    <div className={clsx(classes.container, className)}>
      <div className={classes.rowInput}>
        <SelectInput
          label={lang.vatReturn.form.placeHolderYear}
          className={classes.yearInput}
          notAsync
          containerClass={classes.input}
          allowCreate={false}
          value={year}
          options={yearInputOptions}
          handleChange={handleYearChange} />
        <SelectInput
          className={classes.periodInput}
          notAsync
          allowCreate={false}
          label={lang.accounting.datev.transferMonth}
          value={period}
          readOnly={readOnly}
          options={periodOptions}
          containerClass={classes.periodInput}
          handleChange={setPeriod}
          customComponents={{
            ValueContainer: (props) => {
              const textItems = []
              if (!isLoading) {
                if (selectedContent.includes(content.documents.value)) {
                  textItems.push(lang.accounting.getNewDocumentText(newDocumentsCount))
                }
                if (selectedContent.includes(content.bookingEntries.value)) {
                  textItems.push(lang.accounting.getNewBookingEntriesText(newBookingEntriesCount))
                }
              }

              const text = textItems.join(' / ')
              return (
                <>
                  <components.ValueContainer {...props} />
                  <div>{ isLoading
                    ? '...'
                    : text.length > 0 ? `(${text})` : ''
                  }
                  </div>
                </>
              )
            },
          }}
        />
      </div>
      <Checkbox name='resendText' customClass={classes.checkbox} checked={resend} onChange={setResend}>
        <span className={classes.checkboxLabel}>{lang.accounting.datev.resendText}</span>
      </Checkbox>
      <Checkbox name='sendDocumentType' customClass={classes.checkbox} checked={useDocumentType} onChange={updateUseDocumentType}>
        <span className={classes.checkboxLabel}>{lang.accounting.datev.sendDocumentType}</span>
      </Checkbox>
      <Label className={classes.contentLabel} size="large" >{lang.accounting.transferView.content}</Label>
      <div className={classes.contentCheckboxesContainer}>
        { documentContentCheckboxes }
      </div>
      <Label className={classes.transferLabel} size="large" >{lang.accounting.transferView.toTransfer}</Label>
      { documentTypeCheckboxes }
      <div className={classes.action}>
        <ButtonIconSquare
          disabled={readOnly || isSubmitting || selectedContent.length === 0}
          icon={IconCheck}
          label={isSubmitting ? lang.accounting.buttonTransferProcessingText : lang.accounting.datev.buttonTransferStartText}
          onClick={handleSendClick}
        />
      </div>
      <Label className={classes.contentLabel} size="large" >{lang.accounting.datev.automateText}</Label>
      <div className={classes.automateContainer}>
        <Checkbox name='resendText' customClass={clsx(classes.checkbox, classes.marginLeft)} checked={autoSyncActive} onChange={updateAutoSyncActive} readOnly={!availableContent.includes(content.documents)}>
          <span className={classes.checkboxLabel}>{lang.accounting.allDocuments}</span>
        </Checkbox>
      </div>
    </div>
  )
}

DatevTransferView.propTypes = {
  onSubmit: PropTypes.func,
  className: PropTypes.string,
  availableServiceScopes: PropTypes.arrayOf(PropTypes.string),
  autoSyncActive: PropTypes.bool.isRequired,
  updateAutoSyncActive: PropTypes.func.isRequired,
  useDocumentType: PropTypes.bool.isRequired,
  updateUseDocumentType: PropTypes.func.isRequired,
}

DatevTransferView.defaultProps = {
  availableServiceScopes: [],
}

export default DatevTransferView
